import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { dashboardService, DashboardSummary } from "@/services/dashboardService";
import {
  Users,
  Wallet,
  CalendarDays,
  ArrowRight,
  FileText,
  Banknote,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Clock,
  Plus,
  ArrowUpRight,
  CreditCard,
  UserPlus,
  Calendar,
  BarChart3,
} from "lucide-react";

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await dashboardService.getSummary();
        setSummary(data);
      } catch (error) {
        console.error("Failed to load dashboard summary:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="h-10 w-10 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="h-12 w-12 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center mx-auto mb-3">
            <TrendingDown size={20} className="text-red-500" />
          </div>
          <p className="text-gray-900 dark:text-white font-medium mb-1">Failed to load data</p>
          <p className="text-sm text-gray-500">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  // ==================== ADMIN / HR DASHBOARD ====================
  if (user?.role !== "employee") {
    const stats = [
      {
        label: "Total Employees",
        value: summary.totalEmployees ?? 0,
        change: "+12%",
        trend: "up" as const,
        icon: <Users size={20} />,
        iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
        iconColor: "text-indigo-600 dark:text-indigo-400",
      },
      {
        label: "Pending Leaves",
        value: summary.pendingLeaves ?? 0,
        change: "+5",
        trend: "up" as const,
        icon: <CalendarDays size={20} />,
        iconBg: "bg-amber-50 dark:bg-amber-900/20",
        iconColor: "text-amber-600 dark:text-amber-400",
      },
      {
        label: "Active Loans",
        value: summary.activeLoans ?? 0,
        change: "-2",
        trend: "down" as const,
        icon: <Banknote size={20} />,
        iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
        iconColor: "text-emerald-600 dark:text-emerald-400",
      },
      {
        label: "Monthly Payroll",
        value: dashboardService.formatCurrency(summary.thisMonthPayroll ?? 0, "USD"),
        change: "+8%",
        trend: "up" as const,
        icon: <CreditCard size={20} />,
        iconBg: "bg-violet-50 dark:bg-violet-900/20",
        iconColor: "text-violet-600 dark:text-violet-400",
      },
    ];

    const quickActions = [
      {
        title: "Add Employee",
        description: "Register a new team member",
        icon: <UserPlus size={18} />,
        path: "/employees/add",
        color: "bg-indigo-600 hover:bg-indigo-700",
      },
      {
        title: "Run Payroll",
        description: "Process monthly payroll",
        icon: <CreditCard size={18} />,
        path: "/payroll",
        color: "bg-emerald-600 hover:bg-emerald-700",
      },
      {
        title: "Leave Requests",
        description: "Review pending approvals",
        icon: <Calendar size={18} />,
        path: "/leaves/all",
        color: "bg-amber-600 hover:bg-amber-700",
      },
      {
        title: "View Reports",
        description: "Analytics & insights",
        icon: <BarChart3 size={18} />,
        path: "/reports/analytics",
        color: "bg-violet-600 hover:bg-violet-700",
      },
    ];

    return (
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Welcome back, {user?.firstName}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Here's what's happening with your organization today.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/reports/analytics")}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              <div className="flex items-center gap-2">
                <BarChart3 size={16} />
                Reports
              </div>
            </button>
            <button
              onClick={() => navigate("/employees/add")}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <div className="flex items-center gap-2">
                <Plus size={16} />
                New Employee
              </div>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card hover:shadow-card-hover transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${stat.iconBg}`}>
                  <span className={stat.iconColor}>{stat.icon}</span>
                </div>
                <div
                  className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                    stat.trend === "up"
                      ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
                      : "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400"
                  }`}
                >
                  {stat.trend === "up" ? (
                    <TrendingUp size={12} />
                  ) : (
                    <TrendingDown size={12} />
                  )}
                  {stat.change}
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stat.value}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Quick Actions + Activity */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Quick Actions
              </h3>
              <button
                onClick={() => navigate("/employees")}
                className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-medium flex items-center gap-1"
              >
                View all <ArrowRight size={14} />
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => navigate(action.path)}
                  className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-card hover:shadow-card-hover transition-all text-left"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`h-10 w-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${action.color} transition-colors`}
                    >
                      {action.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {action.title}
                        </h4>
                        <ArrowUpRight
                          size={14}
                          className="text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors flex-shrink-0"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {action.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Overview Panel */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Organization Overview
              </h3>
            </div>
            <div className="p-5 space-y-4">
              {[
                {
                  label: "Active Employees",
                  value: summary.totalEmployees ?? 0,
                  icon: <Users size={16} />,
                  color: "text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 dark:text-indigo-400",
                },
                {
                  label: "Pending Leave Requests",
                  value: summary.pendingLeaves ?? 0,
                  icon: <CalendarDays size={16} />,
                  color: "text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400",
                },
                {
                  label: "Active Loans",
                  value: summary.activeLoans ?? 0,
                  icon: <Banknote size={16} />,
                  color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400",
                },
                {
                  label: "This Month Payroll",
                  value: dashboardService.formatCurrency(
                    summary.thisMonthPayroll ?? 0,
                    "USD"
                  ),
                  icon: <Wallet size={16} />,
                  color: "text-violet-600 bg-violet-50 dark:bg-violet-900/20 dark:text-violet-400",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-8 w-8 rounded-lg flex items-center justify-center ${item.color}`}
                    >
                      {item.icon}
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {item.label}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== EMPLOYEE DASHBOARD ====================
  const employeeData = {
    pendingLeaves: summary.pendingLeaves ?? 0,
    activeLoans: summary.activeLoans ?? 0,
    leaveBalance: 15,
    nextPayday: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
  };

  const employeeStats = [
    {
      label: "Leave Balance",
      value: `${employeeData.leaveBalance} days`,
      sub: "Remaining this year",
      icon: <CalendarDays size={18} />,
      iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
      iconColor: "text-indigo-600 dark:text-indigo-400",
    },
    {
      label: "Pending Leaves",
      value: employeeData.pendingLeaves,
      sub: "Awaiting approval",
      icon: <Clock size={18} />,
      iconBg: "bg-amber-50 dark:bg-amber-900/20",
      iconColor: "text-amber-600 dark:text-amber-400",
    },
    {
      label: "Active Loans",
      value: employeeData.activeLoans,
      sub: "Current loans",
      icon: <Banknote size={18} />,
      iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
      iconColor: "text-emerald-600 dark:text-emerald-400",
    },
    {
      label: "Next Payday",
      value: employeeData.nextPayday.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
      }),
      sub: "Upcoming payment",
      icon: <Wallet size={18} />,
      iconBg: "bg-violet-50 dark:bg-violet-900/20",
      iconColor: "text-violet-600 dark:text-violet-400",
    },
  ];

  const employeeActions = [
    {
      title: "My Payslips",
      description: "View and download salary slips",
      icon: <FileText size={16} />,
      path: "/my-payslips",
      iconBg: "bg-indigo-100 dark:bg-indigo-900/30",
      iconColor: "text-indigo-600 dark:text-indigo-400",
    },
    {
      title: "Apply for Leave",
      description: "Submit new leave request",
      icon: <CalendarDays size={16} />,
      path: "/leaves",
      iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
      iconColor: "text-emerald-600 dark:text-emerald-400",
    },
    {
      title: "Loan Application",
      description: "Apply for salary advance",
      icon: <Banknote size={16} />,
      path: "/loans",
      iconBg: "bg-amber-100 dark:bg-amber-900/30",
      iconColor: "text-amber-600 dark:text-amber-400",
    },
    {
      title: "My Profile",
      description: "Update personal information",
      icon: <Users size={16} />,
      path: "/profile",
      iconBg: "bg-violet-100 dark:bg-violet-900/30",
      iconColor: "text-violet-600 dark:text-violet-400",
    },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">Welcome back, {user?.firstName}</h2>
            <p className="text-indigo-200 text-sm mt-1">
              Here's your personal dashboard overview.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/10">
              <p className="text-indigo-200 text-xs">Today</p>
              <p className="text-white font-medium text-sm">
                {new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {employeeStats.map((stat, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${stat.iconBg}`}>
                <span className={stat.iconColor}>{stat.icon}</span>
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {stat.label}
              </span>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {stat.value}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {employeeActions.map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.path)}
              className="group flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-card hover:shadow-card-hover transition-all text-left"
            >
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${action.iconBg}`}>
                <span className={action.iconColor}>{action.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {action.title}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {action.description}
                </p>
              </div>
              <ChevronRight
                size={16}
                className="text-gray-300 group-hover:text-indigo-500 transition-colors flex-shrink-0"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
